import {
    createAPIRequest,
    createAPIResponse,
    fetcher,
    ListResponse,
    ResponseWrapper,
    UseFetchPaginatedState,
    useFetchPaginatedState,
} from '@tlx/astro-shared';
import useSWRInfinite from 'swr/infinite';
import { EmployeeDTO } from '@Page/SalaryTaxCard/SalaryTaxCard.types';
import { AltinnData } from '@Page/SalaryTaxCard/reducers/altinnHandling';
import useSWR, { SWRResponse } from 'swr';

interface TaxCardAPIOperations {
    fetchTaxCardRows: () => UseFetchPaginatedState<EmployeeDTO>;
}

type FetchEmployeesArgs = {
    year: string;
    query: string;
    sort: string;
    hasQuit?: boolean;
    shouldFetch?: boolean /* always does 2 API calls but only want to fetch quit if hasQuit is true | conditional fetching: https://swr.vercel.app/docs/conditional-fetching*/;
};

export interface EmployeeTaxcardsInfo {
    data: EmployeeDTO[];
    error?: Error;
    isLoading?: boolean;
    hasMore?: boolean;
    loadMore?: () => void;
    refresh?: () => void;
}

// This is for the select-all machinery
function useAllEmployeeIds(params: FetchEmployeesArgs): SWRResponse<ListResponse<EmployeeDTO>> {
    return useSWR<ListResponse<EmployeeDTO>>(getUrl({ params, count: 0, from: 0, idOnly: true }));
}

function useTaxcardsApi(props: FetchEmployeesArgs, altinn: AltinnData): EmployeeTaxcardsInfo {
    const pageSize = 70;
    const response = useSWRInfinite<ListResponse<EmployeeDTO>>(
        (pageIndex: number, previousPageData: ListResponse<EmployeeDTO>) => {
            if (previousPageData && previousPageData.fullResultSize === 0) {
                return null; // no more data left: this stops the swrinfinite-process
            }

            const key = props.shouldFetch
                ? getUrl({
                      params: props,
                      count: pageSize,
                      from: pageSize * pageIndex,
                  })
                : null;
            return [key, altinn.numCardsReceived];
        },
        (arg: [string, number]) => fetcher(arg[0]), // the key of the swr cache is this array, but our fetcher (from Astro) requires simply a url (string)
        { revalidateAll: true },
    );

    const { data, error, isLoading, hasMore } = useFetchPaginatedState<EmployeeDTO>(response);

    function loadMore() {
        if (hasMore && !response.isValidating) {
            response.setSize(response.size + 1);
        }
    }

    function refresh() {
        response.mutate();
    }

    return {
        data,
        error,
        isLoading,
        hasMore,
        loadMore,
        refresh,
    };
}

type urlProps = {
    params: FetchEmployeesArgs;
    count?: number;
    from?: number;
    idOnly?: boolean;
};

function getUrl({ params, count, from, idOnly }: urlProps) {
    const searchParams = new URLSearchParams();
    if (count !== undefined) {
        searchParams.set('count', count.toString());
    }
    if (from !== undefined) {
        searchParams.set('from', from.toString());
    }
    searchParams.set('year', params.year);
    searchParams.set('query', params.query);
    searchParams.set('hasQuit', `${params.hasQuit}`);
    if (idOnly) {
        searchParams.set('fields', 'id');
    } else {
        searchParams.set('fields', 'id,displayName,number,taxcard(*,advanceTaxcards(*))');
    }
    if (params.sort != '') {
        searchParams.set('sort', params.sort);
    }
    return `/v2/salary/tskinternal/taxcard/micro?${searchParams}`;
}

const IS_LOGGED_IN_ALTINN_UNTIL = '/v2/salary/tskinternal/taxcard/isLoggedInAltinnUntil';
const CHECK_WAITING_TAXCARD_APPLICATIONS_URL = '/v2/salary/tskinternal/taxcard/checkTaxcardApplicationStatus';
const LAST_UPDATED_URL = '/v2/salary/tskinternal/taxcard/lastUpdatedDate';

const LAST_UPDATED_ORDER_ID_URL = `/v2/salary/tskinternal/taxcard/lastUpdatedOrderId`;

async function isLoggedInUntil(): Promise<boolean> {
    const request = createAPIRequest(IS_LOGGED_IN_ALTINN_UNTIL, {
        method: 'GET',
    });
    const response = await window.fetch(request);
    const data = await createAPIResponse<ResponseWrapper<boolean>>(request, response);
    return data.value;
}

async function loginToTestAltinn(): Promise<boolean> {
    const url = '/v2/salary/tskinternal/taxcard/loginToTestAltinn';
    return simplePostAPI<boolean>(url);
}

async function logoutFromAltinn(): Promise<string> {
    const url = '/v2/salary/tskinternal/taxcard/logoutFromAltinn';
    return simplePostAPI<string>(url);
}

async function loginAltinnStep1RequestPin(
    reportingCompanyId: number,
    personalIdNumber: string,
    personalAltinnPassword: string,
): Promise<string[]> {
    const url = `/v2/salary/tskinternal/taxcard/loginAltinnStep1RequestPin?reportingCompanyId=${encodeURIComponent(
        reportingCompanyId,
    )}&personalIdNumberPassword=${encodeURIComponent(
        personalIdNumber,
    )}&personalAltinnPassword=${encodeURIComponent(personalAltinnPassword)}`;
    return simplePostAPI<string[]>(url); // the strings are "messages" from the login step
}

async function loginAltinnStep2VerifyPin(
    reportingCompanyId: number,
    personalIdNumber: string,
    personalAltinnPassword: string,
    pin: string,
): Promise<string[]> {
    const url = `/v2/salary/tskinternal/taxcard/loginAltinnStep2VerifyPin?reportingCompanyId=${encodeURIComponent(
        reportingCompanyId,
    )}&personalIdNumberPassword=${encodeURIComponent(personalIdNumber)}&personalAltinnPassword=${encodeURIComponent(
        personalAltinnPassword,
    )}&pin=${encodeURIComponent(pin)}`;
    return simplePostAPI<string[]>(url); // the strings are "messages" from the login step
}

async function fetchTaxcardsFromAltinn(): Promise<string> {
    const url = '/v2/salary/tskinternal/taxcard/fetchTaxcardsFromAltinn';
    return simplePostAPI<string>(url);
}

async function askForTaxcardsFromAltinn(
    askForTheseEmployeeIds: string,
    contactPersonId: number,
    contactEmail: string,
    taxcardYear: number,
    mobilePhone: string,
    countryCodeId: number,
    notificationType: string,
): Promise<boolean> {
    //removing whitespace
    const cleanedMobilePhone = mobilePhone.replaceAll(/\s/g, '');
    const url = '/v2/salary/tskinternal/taxcard/prepareTaxcards';

    const body = JSON.stringify({
        employeeIds: askForTheseEmployeeIds,
        contactEmployeeId: contactPersonId,
        contactEmail,
        taxcardYear,
        cleanedMobilePhone,
        mobilePhoneCountryId: countryCodeId,
        notificationType,
    });

    return simplePostAPI<boolean>(url, body);
}

// arg is comma-separated string of ids
async function deleteTaxcards(deleteTaxcardIds: string): Promise<boolean> {
    const request = createAPIRequest(`/v2/salary/tskinternal/taxcard/deleteTaxcards?ids=${deleteTaxcardIds}`, {
        method: 'DELETE',
        headers: {
            'content-type': 'application/json',
        },
    });
    const response = await window.fetch(request);
    const data = await createAPIResponse<ResponseWrapper<boolean>>(request, response);

    return data.value;
}

async function simplePostAPI<T>(url: string, body = ''): Promise<T> {
    const request = createAPIRequest(url, {
        body: body,
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    });
    const response = await window.fetch(request);
    const data = await createAPIResponse<ResponseWrapper<T>>(request, response);

    return data.value;
}

export {
    type TaxCardAPIOperations,
    useTaxcardsApi,
    loginToTestAltinn,
    logoutFromAltinn,
    loginAltinnStep1RequestPin,
    loginAltinnStep2VerifyPin,
    askForTaxcardsFromAltinn,
    fetchTaxcardsFromAltinn,
    deleteTaxcards,
    isLoggedInUntil,
    useAllEmployeeIds,
    IS_LOGGED_IN_ALTINN_UNTIL,
    CHECK_WAITING_TAXCARD_APPLICATIONS_URL,
    LAST_UPDATED_URL,
    LAST_UPDATED_ORDER_ID_URL,
};
